/**
 * The global period picker of the top bar (docs/07-gui-proposal §2): one choice every screen
 * reads. It writes to the URL through usePeriod, so the address bar always shows what is on
 * screen and a copied link opens the same thing.
 *
 * Việc 49 (docs/13-tasks/02-terminal-share.md#49): tám nút phẳng một hàng thành HAI tầng —
 * nhóm **Ngày · Tháng · Năm** trước, rồi các lựa chọn của đúng nhóm đang chọn:
 * - Ngày: Hôm nay · 7 ngày · khoảng ngày gõ tay;
 * - Tháng: Tháng này · Tháng trước · Quý này · lưới 12 ô tháng của một năm (‹ năm ›);
 * - Năm: một ô mỗi năm có dữ liệu · công tắc "Lũy kế tới dd/mm".
 * Ở màn biết so nhiều kỳ (`MULTI_PERIOD_PATHS`) nhóm Tháng/Năm có thêm công tắc "So sánh nhiều
 * …": bật lên thì mỗi ô thành một nút bật/tắt, và các kỳ được chọn đặt cạnh nhau trên biểu đồ.
 * Tầng nhóm là nút đặc (accent), tầng chi tiết là nút viền (`.seg.soft`) — hai tầng không lẫn nhau.
 */
import { useState } from 'react';
import { useLocation } from 'react-router';

import { usePeriod } from '../hooks/usePeriod.js';
import {
  FIRST_DATA_YEAR,
  isUsableDate,
  orderedRange,
  PRESET_LABELS,
  yearOptions,
} from '../utils/period.js';
import {
  GROUP_LABELS,
  GROUP_PRESETS,
  isYtd,
  MAX_PERIODS,
  monthCells,
  monthItem,
  MULTI_PERIOD_PATHS,
  type PeriodGroup,
  periodGroup,
  selectedTokens,
  toggleToken,
} from '../utils/periodSet.js';

/**
 * Một đầu của kỳ tuỳ chọn.
 *
 * 🚨 Giữ chữ đang gõ trong `draft`: `<input type="date">` phát sự kiện đổi sau MỖI chữ số của phần
 * năm, nên gõ "2026" đi qua 0002 · 0020 · 0202. Trước 13/09/2026 ô này ghi thẳng lên URL, kỳ vô
 * nghĩa ấy rơi về mặc định và kéo luôn ô về ngày khác ⇒ **ô năm gõ không được**. Nay chỉ ngày gõ
 * XONG mới được ghi; rời ô mà còn dở thì trả về đúng ngày kỳ đang xem, không bịa.
 *
 * Không đặt `min`/`max` chéo nhau nữa: gõ ngược chiều thì `orderedRange` đảo lại, người gõ không
 * phải nhớ mình đang sửa đầu nào.
 *
 * Đồng bộ lại `draft` bằng cách đặt state NGAY TRONG LƯỢT VẼ (React: "adjusting state when a prop
 * changes"), không dùng `useEffect` và cũng không dùng `key` để dựng lại ô: dựng lại là mất con
 * trỏ ngay sau khi gõ xong phần năm, tức là lại không gõ tiếp được.
 */
function DateField({
  label,
  value,
  onPick,
}: {
  label: string;
  value: string;
  onPick: (date: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  const [seen, setSeen] = useState(value);
  if (seen !== value) {
    setSeen(value);
    setDraft(value);
  }
  return (
    <input
      type="date"
      value={draft}
      aria-label={label}
      onChange={(event) => {
        setDraft(event.target.value);
        if (isUsableDate(event.target.value)) onPick(event.target.value);
      }}
      onBlur={() => {
        if (!isUsableDate(draft)) setDraft(value);
      }}
    />
  );
}

/*
  `flex-wrap` là lớp tiện ích của Tailwind và tiện ích thắng `@layer components`, nên hàng cuộn
  ngang ở khổ điện thoại phải khai NGAY ĐÂY (docs/13-tasks/01-ui-chrome.md#16 H2): một hàng tự cuộn
  trong khung của nó, thân trang vẫn không bao giờ cuộn ngang. `responsive.css` chỉ giấu thanh cuộn.
*/
const ROW = 'seg max-w-full flex-wrap max-[760px]:flex-nowrap max-[760px]:overflow-x-auto';

const GROUPS: readonly PeriodGroup[] = ['day', 'month', 'year'];

export function PeriodPicker() {
  const { period, periodSet, today, choosePreset, chooseRange, chooseSet } = usePeriod();
  const { pathname } = useLocation();
  const group = periodGroup(period, periodSet, today);
  const tokens = selectedTokens(period, periodSet, today);
  const ytd = isYtd(period, periodSet, today);
  const canCompare = MULTI_PERIOD_PATHS.includes(pathname);

  /* Công tắc "so sánh nhiều kỳ": bật sẵn khi địa chỉ đã mang một tập; bật tay thì giữ cho tới khi
     người đọc tắt — chọn ô thứ hai mới ghi tập lên địa chỉ. */
  const [multiWanted, setMultiWanted] = useState(false);
  const multi = canCompare && group !== 'day' && (periodSet !== null || multiWanted);

  /* Năm của lưới 12 ô tháng — theo tháng đang chọn, đổi được bằng ‹ ›. */
  const latestMonth = group === 'month' ? tokens.at(-1) : undefined;
  const [gridYear, setGridYear] = useState((latestMonth ?? today).slice(0, 4));
  const [seenMonth, setSeenMonth] = useState(latestMonth);
  if (seenMonth !== latestMonth) {
    setSeenMonth(latestMonth);
    if (latestMonth !== undefined) setGridYear(latestMonth.slice(0, 4));
  }

  const pickRange = (from: string, to: string) => {
    const ordered = orderedRange(from, to);
    chooseRange(ordered.from, ordered.to);
  };

  const pickGroup = (next: PeriodGroup) => {
    if (next === group) return;
    if (next === 'day') choosePreset('last7');
    else if (next === 'month') choosePreset('thisMonth');
    else chooseSet('year', [today.slice(0, 4)]);
  };

  /** Bấm một ô năm/tháng: tắt so sánh ⇒ chọn đúng ô ấy; bật ⇒ thêm/bớt ô ấy khỏi tập. */
  const pickToken = (kind: 'year' | 'month', token: string) => {
    /* một năm đứng riêng vẫn là trọn năm như cũ; thêm năm thứ hai là bắt đầu SO SÁNH, và so sánh
       năm mặc định là CÙNG KỲ (việc 68) — muốn trọn 12 tháng thì tắt "Lũy kế" sau đó */
    const cut = ytd || (multi && periodSet === null);
    chooseSet(kind, multi ? toggleToken(tokens, token) : [token], kind === 'year' && cut);
  };

  const full = multi && tokens.length >= MAX_PERIODS;
  const lastYear = today.slice(0, 4);
  const thisDayMonth = `${today.slice(8, 10)}/${today.slice(5, 7)}`;

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 max-[760px]:w-full max-[760px]:min-w-0">
      {/*
        `w-full min-w-0` at phone width is what KEEPS the scrolling rows inside the screen: a flex
        item sized by its content would have grown along with them — measured 12/09/2026, the
        body scrolled to 600 px until that pair existed.

        The chosen range is NOT spelled out here: every page already prints it beside its title.
      */}
      <div className="seg" role="group" aria-label="Kiểu kỳ">
        {GROUPS.map((item) => (
          <button
            key={item}
            type="button"
            aria-pressed={group === item}
            onClick={() => {
              pickGroup(item);
            }}
          >
            {GROUP_LABELS[item]}
          </button>
        ))}
      </div>
      {/* ngăn tầng nhóm với tầng chi tiết — tầng sau là "bên trong" nhóm vừa chọn */}
      <span aria-hidden="true" className="-mx-1 text-[15px] opacity-50 max-[760px]:hidden">
        ›
      </span>

      {GROUP_PRESETS[group].length > 0 && (
        <div className={`${ROW} soft`} role="group" aria-label="Kỳ tính theo hôm nay">
          {GROUP_PRESETS[group].map((preset) => (
            <button
              key={preset}
              type="button"
              aria-pressed={periodSet === null && period.preset === preset}
              onClick={() => {
                choosePreset(preset);
              }}
            >
              {PRESET_LABELS[preset]}
            </button>
          ))}
        </div>
      )}

      {group === 'day' && (
        <span className="field">
          <DateField
            label="Từ ngày"
            value={period.from}
            onPick={(date) => {
              pickRange(date, period.to);
            }}
          />
          <span aria-hidden="true">–</span>
          <DateField
            label="Đến ngày"
            value={period.to}
            onPick={(date) => {
              pickRange(period.from, date);
            }}
          />
        </span>
      )}

      {group === 'month' && (
        <>
          <span className="field" role="group" aria-label="Năm của lưới tháng">
            <button
              type="button"
              className="iconbtn touch"
              aria-label="Năm trước"
              disabled={Number(gridYear) <= FIRST_DATA_YEAR}
              onClick={() => {
                setGridYear(String(Number(gridYear) - 1));
              }}
            >
              ‹
            </button>
            <span className="num min-w-[4ch] text-center text-[var(--nav-ink)]">{gridYear}</span>
            <button
              type="button"
              className="iconbtn touch"
              aria-label="Năm sau"
              disabled={gridYear >= lastYear}
              onClick={() => {
                setGridYear(String(Number(gridYear) + 1));
              }}
            >
              ›
            </button>
          </span>
          <div className={`${ROW} soft`} role="group" aria-label={`Các tháng năm ${gridYear}`}>
            {monthCells(gridYear, today).map((cell) => {
              const on = tokens.includes(cell.token);
              return (
                <button
                  key={cell.token}
                  type="button"
                  aria-pressed={on}
                  disabled={cell.future || (full && !on)}
                  title={cell.future ? 'Tháng chưa tới' : undefined}
                  onClick={() => {
                    pickToken('month', cell.token);
                  }}
                >
                  {cell.label}
                </button>
              );
            })}
          </div>
        </>
      )}

      {group === 'year' && (
        <>
          <div className={`${ROW} soft`} role="group" aria-label="Các năm">
            {[...yearOptions(today)].reverse().map((year) => {
              const on = tokens.includes(year);
              return (
                <button
                  key={year}
                  type="button"
                  aria-pressed={on}
                  disabled={full && !on}
                  onClick={() => {
                    pickToken('year', year);
                  }}
                >
                  {year}
                </button>
              );
            })}
          </div>
          <div className="seg soft" role="group" aria-label="Lũy kế">
            <button
              type="button"
              aria-pressed={ytd}
              title={`Mỗi năm tính từ 01/01 tới ${thisDayMonth} — so năm đang chạy dở với cùng kỳ các năm trước`}
              onClick={() => {
                chooseSet('year', tokens.length > 0 ? tokens : [lastYear], !ytd);
              }}
            >
              Lũy kế tới {thisDayMonth}
            </button>
          </div>
        </>
      )}

      {canCompare && group !== 'day' && (
        <label className="field cursor-pointer select-none">
          <input
            type="checkbox"
            checked={multi}
            onChange={(event) => {
              setMultiWanted(event.target.checked);
              /* tắt ⇒ về đúng một kỳ: kỳ mới nhất của tập, kỳ cả trang đang đứng */
              if (!event.target.checked && periodSet !== null) {
                const latest = tokens.at(-1);
                if (latest !== undefined) chooseSet(periodSet.kind, [latest], ytd);
              }
            }}
          />
          So sánh nhiều {group === 'year' ? 'năm' : 'tháng'}
        </label>
      )}

      {multi && group === 'month' && tokens.length > 0 && (
        <span className="text-[12.5px] whitespace-nowrap opacity-80">
          Đang so: {tokens.map((token) => monthItem(token)?.label ?? token).join(' · ')}
          {full && ` (tối đa ${String(MAX_PERIODS)})`}
        </span>
      )}
      {multi && group === 'year' && full && (
        <span className="text-[12.5px] whitespace-nowrap opacity-80">tối đa {MAX_PERIODS} năm</span>
      )}
    </div>
  );
}
